import {type Payments, type Card} from '@square/web-payments-sdk-types';
import {$qsAll} from '../../../../@shared/ts/dom';
import {type SquareGateway} from '../../models/square';
import {Feature} from '../../reducers/environmentReducer';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {createVerifyBuyerDetails, handleTokenResult} from './square';
import cardBadgeURL from '../../../img/badge/card.svg';
import cardImageURL from '../../../img/marks/credit-card-regular.svg';
import {FeatureFlag} from '../../../../@type/features';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';

function initSquareCardMethod(): SquareGateway {
	let cardElement: Card | null = null;
	let activated = false;

	const config: GatewayConfiguration = {
		name: Feature.metadata<string>(FeatureFlag.SQUARE_CARD_GATEWAY, 'title') ?? getLocaleText('Card'),
		gatewayId: 'peachpay_square_card',
		description: '',
		assets: {
			title: {src: cardImageURL},
			badge: {src: cardBadgeURL},
		},
	};

	return {
		config,
		async activate(payments: Payments) {
			if (activated) {
				return;
			}

			activated = true;

			const squareCardOptions = {
				style: {
					'.input-container': {
						borderWidth: '1px',
					},
				},
			};

			$qsAll('div.pp-pm-saved-option[data-gateway="peachpay_square_card"]', $el => {
				$el.style.padding = '0';
				$el.style.border = 'unset';
			});

			cardElement = await payments.card(squareCardOptions);
			cardElement.attach('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_square_card"]').catch(() => {/**/});
			cardElement?.recalculateSize();

			let previousPaymentMethod = '';
			store.subscribe(() => {
				const currentPaymentMethod = PaymentConfiguration.selectedGateway();
				if (currentPaymentMethod === previousPaymentMethod) {
					return;
				}

				previousPaymentMethod = currentPaymentMethod;

				cardElement?.recalculateSize();
			});
		},
		async tokenize(payments: Payments) {
			if (!cardElement) {
				return [null, null, {
					paymentStatus: 'creationfailed',
					note: 'Square Card element not found while attempting to tokenize card.',
				}];
			}

			let tokenResult;
			try {
				tokenResult = await cardElement.tokenize();
			} catch (error: unknown) {
				return [null, null, {
					paymentStatus: 'creationfailed',
					note: `Square Card element tokenization failed with error: ${(error as Error).message}`,
				}];
			}

			const [sourceId, _, transactionUpdate] = handleTokenResult(tokenResult);
			if (!sourceId) {
				return [null, null, transactionUpdate];
			}

			// Square SCA/3DS support. https://developer.squareup.com/docs/sca-overview
			try {
				const response = await payments.verifyBuyer(sourceId, createVerifyBuyerDetails());

				return [sourceId, response?.token ?? null, {}];
			} catch (error: unknown) {
				return [null, null, {
					paymentStatus: 'creationfailed',
					note: `Verifying Square buyer failed with error: ${(error as Error).message}`,
				}];
			}
		},
	};
}

export {initSquareCardMethod};
